import React, { useState, useEffect } from 'react';
import { apiBase, resolveOrgId } from './utils/authHeaders';
import { MapPin, Clock, CheckCircle, AlertCircle, User, DollarSign, Package, Navigation, Phone } from 'lucide-react';

const DeliveryManagement = () => {
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAssignDriverModal, setShowAssignDriverModal] = useState(false);
  const [viewMode, setViewMode] = useState('orders'); // orders, drivers, map
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [orderForm, setOrderForm] = useState({ customer_name: '', customer_phone: '', delivery_address: '', order_amount: '', delivery_fee: '', payment_status: 'pending' });
  const [driverForm, setDriverForm] = useState({ first_name: '', last_name: '', phone: '', vehicle_make: '', vehicle_model: '', vehicle_plate: '' });

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'X-Organization-Id': localStorage.getItem('current_organization_id'),
  });

  const submitOrder = async () => {
    const body = { ...orderForm, order_amount: parseFloat(orderForm.order_amount) || 0, delivery_fee: parseFloat(orderForm.delivery_fee) || 0 };
    const res = await fetch(`${API_URL}/api/delivery/orders`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
    if (res.ok) { setShowAddOrder(false); setOrderForm({ customer_name: '', customer_phone: '', delivery_address: '', order_amount: '', delivery_fee: '', payment_status: 'pending' }); fetchOrders(); }
  };

  const submitDriver = async () => {
    const res = await fetch(`${API_URL}/api/delivery/drivers`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(driverForm) });
    if (res.ok) { setShowAddDriver(false); setDriverForm({ first_name: '', last_name: '', phone: '', vehicle_make: '', vehicle_model: '', vehicle_plate: '' }); fetchDrivers(); }
  };

  const API_URL = apiBase();

  useEffect(() => {
    fetchOrders();
    fetchDrivers();
    fetchAssignments();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('current_organization_id');
      
      const response = await fetch(`${API_URL}/api/delivery/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('current_organization_id');
      
      const response = await fetch(`${API_URL}/api/delivery/drivers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDrivers(data);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('current_organization_id');
      
      const response = await fetch(`${API_URL}/api/delivery/assignments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAssignments(data);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const getOrderStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-100',
      confirmed: 'text-blue-600 bg-blue-100',
      preparing: 'text-purple-600 bg-purple-100',
      ready_for_pickup: 'text-indigo-600 bg-indigo-100',
      out_for_delivery: 'text-orange-600 bg-orange-100',
      delivered: 'text-green-600 bg-green-100',
      cancelled: 'text-red-600 bg-red-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getDriverStatusColor = (status) => {
    const colors = {
      available: 'text-green-600 bg-green-100',
      busy: 'text-orange-600 bg-orange-100',
      offline: 'text-gray-600 bg-gray-100',
      on_break: 'text-blue-600 bg-blue-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getDeliveryStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-100',
      assigned: 'text-blue-600 bg-blue-100',
      picked_up: 'text-purple-600 bg-purple-100',
      in_transit: 'text-orange-600 bg-orange-100',
      delivered: 'text-green-600 bg-green-100',
      failed: 'text-red-600 bg-red-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const activeOrders = orders.filter(order => 
    !['delivered', 'cancelled'].includes(order.order_status)
  );
  const availableDrivers = drivers.filter(driver => driver.status === 'available');
  const totalRevenue = orders.reduce((sum, order) => 
    sum + (order.payment_status === 'paid' ? order.total_amount : 0), 0
  );

  const OrderCard = ({ order }) => {
    const assignment = assignments.find(a => a.order_id === order.id);
    const driver = assignment ? drivers.find(d => d.id === assignment.driver_id) : null;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold">Order #{order.order_number}</h3>
            <p className="text-sm text-gray-600">{order.customer_name}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getOrderStatusColor(order.order_status)}`}>
            {order.order_status.replace('_', ' ')}
          </span>
        </div>

        <div className="space-y-3">
          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="w-4 h-4 mr-2" />
            <span className="truncate">{order.delivery_address}</span>
          </div>
          
          <div className="flex items-center text-sm text-gray-600">
            <Phone className="w-4 h-4 mr-2" />
            <span>{order.customer_phone}</span>
          </div>

          <div className="flex items-center text-sm text-gray-600">
            <Clock className="w-4 h-4 mr-2" />
            <span>Est. {order.estimated_delivery_time ? new Date(order.estimated_delivery_time).toLocaleTimeString() : 'N/A'}</span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Total: £{order.total_amount.toFixed(2)}</span>
            <span className={`text-sm px-2 py-1 rounded ${order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {order.payment_status}
            </span>
          </div>

          {driver && (
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2 text-gray-600" />
                <span className="text-sm font-medium">{driver.first_name} {driver.last_name}</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${getDeliveryStatusColor(assignment.status)}`}>
                {assignment.status.replace('_', ' ')}
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2 mt-4">
          {order.order_status === 'confirmed' && (
            <button 
              onClick={() => { setSelectedOrder(order); setShowAssignDriverModal(true); }}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            >
              Assign Driver
            </button>
          )}
          <button className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200">
            View Details
          </button>
        </div>
      </div>
    );
  };

  const DriverCard = ({ driver }) => {
    const activeAssignments = assignments.filter(a => 
      a.driver_id === driver.id && !['delivered', 'failed'].includes(a.status)
    );

    return (
      <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold">{driver.first_name} {driver.last_name}</h3>
            <p className="text-sm text-gray-600">{driver.phone}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDriverStatusColor(driver.status)}`}>
            {driver.status}
          </span>
        </div>

        <div className="space-y-3">
          <div className="text-sm">
            <span className="text-gray-600">Vehicle:</span>
            <p className="font-medium">{driver.vehicle_make} {driver.vehicle_model} ({driver.vehicle_year})</p>
          </div>

          <div className="text-sm">
            <span className="text-gray-600">License Plate:</span>
            <p className="font-medium">{driver.vehicle_plate}</p>
          </div>

          <div className="text-sm">
            <span className="text-gray-600">Total Deliveries:</span>
            <p className="font-medium">{driver.total_deliveries}</p>
          </div>

          <div className="text-sm">
            <span className="text-gray-600">Rating:</span>
            <p className="font-medium">{driver.rating ? `${driver.rating.toFixed(1)}/5` : 'Not rated'}</p>
          </div>

          <div className="text-sm">
            <span className="text-gray-600">Active Orders:</span>
            <p className="font-medium">{activeAssignments.length}</p>
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-4">
          <button className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200">
            View Schedule
          </button>
          <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
            Track Location
          </button>
        </div>
      </div>
    );
  };

  const AssignDriverModal = () => {
    const [selectedDriver, setSelectedDriver] = useState('');

    if (!selectedOrder) return null;

    const handleAssignDriver = async () => {
      if (!selectedDriver) return;

      try {
        const token = localStorage.getItem('token');
        const orgId = localStorage.getItem('current_organization_id');
        
        const response = await fetch(`${API_URL}/api/delivery/assignments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Organization-Id': orgId
          },
          body: JSON.stringify({
            order_id: selectedOrder.id,
            driver_id: selectedDriver
          })
        });

        if (response.ok) {
          setShowAssignDriverModal(false);
          fetchAssignments();
          fetchOrders();
          setSelectedOrder(null);
        }
      } catch (error) {
        console.error('Error assigning driver:', error);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <h2 className="text-2xl font-bold mb-6">Assign Driver</h2>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Order: #{selectedOrder.order_number}</p>
            <p className="text-sm text-gray-600">Customer: {selectedOrder.customer_name}</p>
            <p className="text-sm text-gray-600">Address: {selectedOrder.delivery_address}</p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Driver</label>
              <select aria-label="Select Driver" 
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
              >
                <option value="">Choose a driver</option>
                {availableDrivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.first_name} {driver.last_name} - {driver.vehicle_make} {driver.vehicle_model}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button 
              onClick={() => setShowAssignDriverModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button 
              onClick={handleAssignDriver}
              disabled={!selectedDriver}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Assign Driver
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Delivery Management</h1>
        <div className="flex space-x-2">
          <button onClick={() => setShowAddOrder(true)} data-testid="delivery-add-order-btn" className="px-4 py-2 rounded-md bg-green-600 text-white">+ Order</button>
          <button onClick={() => setShowAddDriver(true)} data-testid="delivery-add-driver-btn" className="px-4 py-2 rounded-md bg-purple-600 text-white">+ Driver</button>
          <button 
            onClick={() => setViewMode('orders')}
            className={`px-4 py-2 rounded-md ${viewMode === 'orders' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Orders
          </button>
          <button 
            onClick={() => setViewMode('drivers')}
            className={`px-4 py-2 rounded-md ${viewMode === 'drivers' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Drivers
          </button>
          <button 
            onClick={() => setViewMode('map')}
            className={`px-4 py-2 rounded-md ${viewMode === 'map' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Live Map
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-full">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Active Orders</p>
              <p className="text-2xl font-semibold">{activeOrders.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-full">
              <User className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Available Drivers</p>
              <p className="text-2xl font-semibold">{availableDrivers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Avg Delivery Time</p>
              <p className="text-2xl font-semibold">32 min</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-full">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Today's Revenue</p>
              <p className="text-2xl font-semibold">£{totalRevenue.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'orders' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {orders.map(order => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      )}

      {viewMode === 'drivers' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {drivers.map(driver => (
            <DriverCard key={driver.id} driver={driver} />
          ))}
        </div>
      )}

      {viewMode === 'map' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-center h-96 bg-gray-100 rounded">
            <div className="text-center">
              <Navigation className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Live delivery tracking map</p>
              <p className="text-sm text-gray-500 mt-2">Integration with mapping service required</p>
            </div>
          </div>
        </div>
      )}

      {showAssignDriverModal && <AssignDriverModal />}

      {showAddOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="delivery-order-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 space-y-3">
            <h2 className="text-2xl font-bold">New Delivery Order</h2>
            <input placeholder="Customer name" value={orderForm.customer_name} onChange={e => setOrderForm({ ...orderForm, customer_name: e.target.value })} data-testid="delivery-order-name" className="w-full border border-gray-300 rounded-md px-3 py-2" />
            <input placeholder="Phone" value={orderForm.customer_phone} onChange={e => setOrderForm({ ...orderForm, customer_phone: e.target.value })} data-testid="delivery-order-phone" className="w-full border border-gray-300 rounded-md px-3 py-2" />
            <input placeholder="Delivery address" value={orderForm.delivery_address} onChange={e => setOrderForm({ ...orderForm, delivery_address: e.target.value })} data-testid="delivery-order-address" className="w-full border border-gray-300 rounded-md px-3 py-2" />
            <div className="grid grid-cols-2 gap-3">
              <input type="number" placeholder="Order amount" value={orderForm.order_amount} onChange={e => setOrderForm({ ...orderForm, order_amount: e.target.value })} data-testid="delivery-order-amount" className="border border-gray-300 rounded-md px-3 py-2" />
              <input type="number" placeholder="Delivery fee" value={orderForm.delivery_fee} onChange={e => setOrderForm({ ...orderForm, delivery_fee: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2" />
            </div>
            <select value={orderForm.payment_status} onChange={e => setOrderForm({ ...orderForm, payment_status: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2">
              <option value="pending">Payment pending</option><option value="paid">Paid</option>
            </select>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowAddOrder(false)} className="px-4 py-2 text-gray-600 border rounded-md">Cancel</button>
              <button onClick={submitOrder} disabled={!orderForm.customer_name || !orderForm.delivery_address} data-testid="delivery-order-submit" className="px-4 py-2 bg-green-600 text-white rounded-md disabled:opacity-50">Create</button>
            </div>
          </div>
        </div>
      )}

      {showAddDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="delivery-driver-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 space-y-3">
            <h2 className="text-2xl font-bold">New Driver</h2>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="First name" value={driverForm.first_name} onChange={e => setDriverForm({ ...driverForm, first_name: e.target.value })} data-testid="delivery-driver-first" className="border border-gray-300 rounded-md px-3 py-2" />
              <input placeholder="Last name" value={driverForm.last_name} onChange={e => setDriverForm({ ...driverForm, last_name: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2" />
            </div>
            <input placeholder="Phone" value={driverForm.phone} onChange={e => setDriverForm({ ...driverForm, phone: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2" />
            <div className="grid grid-cols-3 gap-3">
              <input placeholder="Make" value={driverForm.vehicle_make} onChange={e => setDriverForm({ ...driverForm, vehicle_make: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2" />
              <input placeholder="Model" value={driverForm.vehicle_model} onChange={e => setDriverForm({ ...driverForm, vehicle_model: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2" />
              <input placeholder="Plate" value={driverForm.vehicle_plate} onChange={e => setDriverForm({ ...driverForm, vehicle_plate: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2" />
            </div>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowAddDriver(false)} className="px-4 py-2 text-gray-600 border rounded-md">Cancel</button>
              <button onClick={submitDriver} disabled={!driverForm.first_name} data-testid="delivery-driver-submit" className="px-4 py-2 bg-purple-600 text-white rounded-md disabled:opacity-50">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryManagement;
